import * as React from 'react';
import { observer } from 'mobx-react';
import { action, observable, when } from 'mobx';
import { Button, Input } from 'reactstrap';
import Draggable from 'react-draggable';

import { ApiUrls } from '@app/AppConstants/ApiUrls';
import { SignalREvents, SignalRMethods } from '@app/AppConstants/SignalR';
import { applyDecorators } from "@app/Helpers/Decorator";
import { RosterCell } from '@app/Models';
import { DutyValidationErrorModel, EnumModel } from '@app/Models/WebApiModels';

import ApiService from '@app/Services/ApiService';
import DateTimeService from '@app/Services/DateTimeService';
import { signalRService } from '@app/Services/SignalRService';
import { navigationStore } from '@app/Stores/NavigationStore';

import { Icon } from '@app/Components';
import { AiRating, ExplainUpdateCitation, ExplainUpdateStatus, ExplainUpdateType, PersonDayErrorExplainRequest, PersonDayErrorExplainRequestItem, PersonDayErrorExplainRequestItemType } from './ExplainErrorModels';
import { ExplainErrorChatActionButton, ExplainErrorChatActionSelect } from './ExplainErrorChatActions';
import "./_explain-error-chat.scss";

type ExplainErrorChatProps = {
    error: DutyValidationErrorModel;
    rosterCell: RosterCell;
    onClose: () => void;
    onNavigateToPerson?: (personId: string, date: Date) => Promise<void>;
};

@observer
export default class ExplainErrorChat extends React.Component<ExplainErrorChatProps> {
    @observable private _messages: PersonDayErrorExplainRequestItem[] = [];
    @observable private _input: string = "";
    private _chatId: string | null = null;
    @observable private _lastReplyStatus: ExplainUpdateStatus = ExplainUpdateStatus.None;
    @observable private _activeRating: AiRating = AiRating.None;
    @observable private _explainModels: EnumModel[] = [];
    @observable private _selectedExplainModelCode: string = '';

    constructor(props: ExplainErrorChatProps) {
        super(props);
        applyDecorators(this);
    }

    componentDidMount() {
        signalRService.subscribeToGroup(SignalREvents.aiExplainValidationMessageReply, this._getReply);
        void this._init();

        when(
            () => !navigationStore.isHomePage,
            () => this.props.onClose()
        );
    }

    componentWillUnmount() {
        this._cancelExplanation();
        signalRService.unsubscribeFromGroup(SignalREvents.aiExplainValidationMessageReply, this._getReply);
    }

    render() {
        const { rosterCell: { person, date }, onClose, onNavigateToPerson } = this.props;

        const isLoading = this._lastReplyStatus === ExplainUpdateStatus.Loading;
        const showRating = this._lastReplyStatus === ExplainUpdateStatus.End;
        const showRegenerate = this._lastReplyStatus === ExplainUpdateStatus.Cancel || this._lastReplyStatus === ExplainUpdateStatus.Error || this._lastReplyStatus === ExplainUpdateStatus.End;
        const showModelSelect = this._lastReplyStatus === ExplainUpdateStatus.Cancel || this._lastReplyStatus === ExplainUpdateStatus.Error || this._lastReplyStatus === ExplainUpdateStatus.End;

        return (
            <Draggable handle=".chat-header" bounds="body">
                <div className="chat">
                    <div className="chat-header">
                        <div className="close-btn" onClick={onClose}>
                            <Icon name="times" />
                        </div>
                        <span title="Click to navigate" onClick={() => onNavigateToPerson?.(person.id, date)}>
                            {person.code}, {DateTimeService.toCustomUiFormat(date, 'D MMM YYYY')}
                        </span>
                    </div>

                    <div className="chat-messages customScrollbar">
                        {this._messages.map((msg, i) => {
                            const role = msg.type === PersonDayErrorExplainRequestItemType.Input || msg.type === PersonDayErrorExplainRequestItemType.User ? 'user' : 'bot';
                            const classNames = ['chat-message', role];
                            if (msg.isError) classNames.push('error');

                            const title = msg.type === PersonDayErrorExplainRequestItemType.Input ? 'Message To Explain' : msg.type === PersonDayErrorExplainRequestItemType.User ? 'You' : 'Crew Page';

                            return (
                                <div key={i} className={classNames.join(' ')}>
                                    <div className="chat-message-content">
                                        <span className="chat-message-title">{title}</span>
                                        {this.processText(msg)}
                                    </div>
                                </div>
                            );
                        })}
                        <div className="chat-interactions">
                            {showRating && (
                                <>
                                    <ExplainErrorChatActionButton
                                        iconName="thumbs-up"
                                        title="The answer is useful to me"
                                        isDisabled={this._activeRating !== AiRating.None}
                                        noSolidIcon={this._activeRating !== AiRating.Like}
                                        onClick={() => this._onRateTheReply(AiRating.Like)}
                                    />
                                    <ExplainErrorChatActionButton
                                        iconName="thumbs-down"
                                        title="The answer is not so good"
                                        isDisabled={this._activeRating !== AiRating.None}
                                        noSolidIcon={this._activeRating !== AiRating.Dislike}
                                        onClick={() => this._onRateTheReply(AiRating.Dislike)}
                                    />
                                </>
                            )}
                            {showRegenerate && (
                                <ExplainErrorChatActionButton
                                    iconName="sync-alt"
                                    title="Regenerate explanation"
                                    onClick={this._regenerateExplanation}
                                />
                            )}
                            {showModelSelect && (
                                <ExplainErrorChatActionSelect
                                    iconName="magic"
                                    title="Change AI model"
                                    options={this._explainModels}
                                    value={this._selectedExplainModelCode}
                                    onSelect={this._onSelectModel}
                                />
                            )}
                        </div>
                        {isLoading && <div className="chat-message loading" />}
                    </div>
                    {/* <div className="chat-controls">
                        <Input
                            placeholder="Enter the question..."
                            className="form-control-sm"
                            value={this._input}
                            onChange={(e) => {
                                this._input = e.target.value;
                            }}
                            onKeyDown={this._onKeyDown}
                        />
                        <Button size="sm" color="info" onClick={this._sendMessage}>
                            <Icon name="paper-plane" />
                        </Button> 
                    </div>*/}

                    {isLoading && (
                        <div className="chat-cancel-task">
                            <Button size="sm" color="info" onClick={this._cancelExplanation}>
                                Stop explanation
                            </Button>
                        </div>
                    )}
                    <div className="chat-footer">AI can make mistakes. Check important info.</div>
                </div>
            </Draggable>
        );
    }

    private async _init() {
        await this._loadExplainModels();

        const inputMessage = {
            type: PersonDayErrorExplainRequestItemType.Input,
            content: this.props.error.message,
            citations: null
        };
        void this._sendMessage(inputMessage);
    }

    @action
    private async _loadExplainModels() {
        const { data } = await ApiService.getTypedData<EnumModel[]>(ApiUrls.DutyValidationExplainModelsUrl);
        this._explainModels = data;

        if (this._explainModels.length > 0) {
            this._selectedExplainModelCode = this._explainModels[0].code;
        }
    }

    @action
    private async _sendMessage(message: PersonDayErrorExplainRequestItem) {
        this._messages.push(message);
        await this._sendSignalRNotification();
    }

    @action
    private async _sendSignalRNotification() {
        this._lastReplyStatus = ExplainUpdateStatus.Loading;

        const { person, date } = this.props.rosterCell;
        const request: PersonDayErrorExplainRequest = {
            modelName: this._selectedExplainModelCode,
            personId: person.id,
            date: date,
            request: this._messages
        };
        this._chatId = await signalRService.sendNotification(SignalRMethods.explainValidationError, request) as string;
    }

    @action.bound
    private _regenerateExplanation() {
        const lastMessage = this._messages[this._messages.length - 1];
        if (lastMessage.type === PersonDayErrorExplainRequestItemType.Assistant) {
            this._messages.pop();
        }

        this._activeRating = AiRating.None;
        void this._sendSignalRNotification();
    }

    @action.bound
    private _cancelExplanation() {
        this._lastReplyStatus = ExplainUpdateStatus.Cancel;
        signalRService.sendNotification(SignalRMethods.stopExplanation, this._chatId);
    }

    @action.bound
    private _getReply(_: string, msg: ExplainUpdateType) {
        if (!this._chatId) return;

        let message: PersonDayErrorExplainRequestItem | null = null;
        for (let i = this._messages.length - 1; i >= 0; i--) {
            if (this._messages[i].type === PersonDayErrorExplainRequestItemType.Assistant) {
                message = this._messages[i];
                break;
            }
        }

        if (!message) {
            message = { type: PersonDayErrorExplainRequestItemType.Assistant, content: '', isError: msg.type === 'Error', citations: null };
            this._messages.push(message);
            message = this._messages[this._messages.length - 1];
        }

        if (msg.type === 'Chunk') {
            message.content += msg.content;
        }
        
        if (msg.type === 'Citations') {
            message.citations = msg.citations || null;
        }

        if (msg.type === 'Error') {
            message.content = msg.content || '';
            message.isError = true;
            this._lastReplyStatus = ExplainUpdateStatus.Error;
        }

        if (msg.type === 'End') {
            this._lastReplyStatus = ExplainUpdateStatus.End;
        }
    }

    @action
    private async _onRateTheReply(rating: AiRating) {
        this._activeRating = rating;

        if (rating == AiRating.Like){
            await ApiService.postData(ApiUrls.LlmLikeUrl + '/' + this._chatId, null);
        }
        if (rating == AiRating.Dislike){
            await ApiService.postData(ApiUrls.LlmDislikeUrl + '/' + this._chatId, null);
        }
    }

    @action.bound
    private _onSelectModel(value: string) {
        this._selectedExplainModelCode = value;
        this._regenerateExplanation();
    }

    @action.bound
    private _onKeyDown(e: React.KeyboardEvent) {
        if (e.key === 'Enter') {
            e.preventDefault();
            const userMessage = {
                type: PersonDayErrorExplainRequestItemType.User,
                content: this._input,
                citations: null
            };
            void this._sendMessage(userMessage);
        }
    }

    parseTable = (lines: string[]) => {
        const headers = lines[0].split("|").map((h) => h.trim()).filter(Boolean);
        const rows = lines.slice(2).map((row) =>
            row.split("|").map((cell) => cell.trim()).filter(Boolean)
        );

        return (
            <table className="border-collapse border border-gray-400 w-full my-2">
                <thead>
                    <tr>
                        {headers.map((header, i) => (
                            <th key={i} className="border p-2 bg-gray-200">{header}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i}>
                            {row.map((cell, j) => (
                                <td key={j} className="border p-2">{cell}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    replaceCitations = (text: string, citations: ExplainUpdateCitation[] | null) => {
        const parts = text.split(/(\[doc\d+\])/g);

        return parts.map((part, index) => {
            const match = part.match(/\[(doc\d+)\]/);
            if (match && citations) {
                const doc = citations.find(d => d.assumedReferenceName === match[0]);
                if (doc) {
                    return (
                        <span key={index} title={doc.content} style={{ textDecoration: "underline", cursor: "help" }}>
                            ({doc.title})
                        </span>
                    );
                }
            }
            return part;
        });
    };

    processText = (msg: PersonDayErrorExplainRequestItem) => {
        const lines = msg.content.split("\n");
        let result = [];
        let tableBuffer: string[] = [];
        let listBuffer: string[] = [];

        lines.forEach((line, index) => {
            if (line.startsWith("|")) {
                tableBuffer.push(line);
            } else {
                if (tableBuffer.length > 0) {
                    result.push(<div key={`table-${index}`}>{this.parseTable(tableBuffer)}</div>);
                    tableBuffer = [];
                }
                if (line === "---") {
                    result.push(<hr key={index} className="char-hr" />);
                    return;
                } else if (line.startsWith("- ")) {
                    listBuffer.push(line.substring(2));
                    return;
                } else if (listBuffer.length > 0) {
                    result.push(
                        <ul key={`list-${index}`} className="list-disc ml-6">
                            {listBuffer.map((item, i) => (
                                <li key={i}>{item}</li>
                            ))}
                        </ul>
                    );
                    listBuffer = [];
                }

                if (line.startsWith("#### ")) {
                    result.push(<h4 key={index}>{line.substring(4)}</h4>);
                } else if (line.startsWith("### ")) {
                    result.push(<h3 key={index}>{line.substring(4)}</h3>);
                } else if (line.startsWith("## ")) {
                    result.push(<h2 key={index}>{line.substring(3)}</h2>);
                } else if (line.startsWith("# ")) {
                    result.push(<h1 key={index}>{line.substring(2)}</h1>);
                } else if (/\*\*(.*?)\*\*/.test(line)) {
                    const parts = line.split(/\*\*(.*?)\*\*/g);
                    result.push(
                        <p key={index}>
                            {parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part))}
                        </p>
                    );
                } else if (/\*(.*?)\*/.test(line)) {
                    const parts = line.split(/\*(.*?)\*/g);
                    result.push(
                        <p key={index}>
                            {parts.map((part, i) => (i % 2 === 1 ? <em key={i}>{part}</em> : part))}
                        </p>
                    );
                } else if (/__(.*?)__/.test(line)) {
                    const parts = line.split(/__(.*?)__/g);
                    result.push(
                        <p key={index}>
                            {parts.map((part, i) => (i % 2 === 1 ? <u key={i}>{part}</u> : part))}
                        </p>
                    );
                } else if (/~~(.*?)~~/.test(line)) {
                    const parts = line.split(/~~(.*?)~~/g);
                    result.push(
                        <p key={index}>
                            {parts.map((part, i) => (i % 2 === 1 ? <del key={i}>{part}</del> : part))}
                        </p>
                    );
                } else {
                    result.push(<p key={index}>{this.replaceCitations(line, msg.citations)}</p>);
                }
            }
        });

        if (listBuffer.length > 0) {
            result.push(
                <ul key={`list-lst`} className="list-disc ml-6">
                    {listBuffer.map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ul>
            );
        }

        if (tableBuffer.length > 0) {
            result.push(<div key={`table-final`}>{this.parseTable(tableBuffer)}</div>);
        }

        return result;
    };
}

export const CONTAINER_ID = 'chat-container';

export class ChatContainer extends React.Component {
    render() {
        return <div id={CONTAINER_ID} />;
    }
}